import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ProductApiService } from './api/product-api.service';

/**
 * 搜索索引中的商品条目。
 * 全部来自真实的分类接口数据（单一数据源），搜索不会复制商品数据。
 */
export interface SearchProduct {
  id: number;
  name: string;
  description: string;
  icon: string;
  tags: string[];
  keywords: string;
  /** 接口数据自带的别名（如 netflix → ["奈飞","网飞","nf"]），参与二级匹配 */
  aliases: string[];
  categoryName: string;
  /** 首要分类 id（来自商品的 category_id，定位时用于跳转到真实分类） */
  categoryId: number;
}

/**
 * 商品搜索别名表（内置兜底配置）。
 * key 使用「去掉空格的小写商品名」，方便未来扩充：
 * 只需在对应 key 下追加别名即可，不需要改搜索逻辑。
 *
 * 接口数据里的 aliases 字段（如 netflix → ["奈飞","网飞","nf"]）与这张
 * 配置表会合并参与匹配：数据别名不够时配置兜底，两边都改不了逻辑。
 */
export const PLATFORM_SEARCH_ALIASES: Record<string, string[]> = {
  youtube: ['油管', 'yt', 'youtube premium', 'yt premium'],
  netflix: ['奈飞', '网飞', 'nf', 'netflix 会员'],
  'disney+': ['disney', '迪士尼', 'disney plus', 'dlp'],
  'chatgptplus': ['chatgpt', 'gpt', 'gpt plus', 'gpt4', 'gpt-4', 'openai', 'chat gpt'],
  chatgpt: ['gpt', 'openai', 'chat gpt'],
  gemini: ['google ai', '谷歌ai', '谷歌 ai', 'bard', 'gemini pro'],
  spotify: ['声田', '声破天', 'spo'],
  'primevideo': ['amazon', '亚马逊', 'prime', 'prime video'],
  max: ['hbo', 'hbo max', 'hbo max会员'],
  midjourney: ['mj', 'mid journey', 'ai绘画', 'ai画画'],
  claude: ['anthropic', 'claude ai'],
  perplexity: ['perplexity ai', 'plexity'],
  adobe: ['adobe全家桶', 'adobe cc', 'ps', 'photoshop'],
  'googledrive': ['google 网盘', '谷歌网盘', 'google one'],
  'googleone': ['google 网盘', '谷歌网盘', 'google drive'],
  icloud: ['icloud+', '苹果云盘', 'apple cloud'],
  crunchyroll: ['crunchy roll', '动漫'],
};

@Injectable({
  providedIn: 'root',
})
export class PlatformCatalogService {
  constructor(private api: ProductApiService) {}

  /** 分类原始数据（与 /buy 页面展示共用同一份） */
  categories: any[] = [];

  /** 扁平化后的搜索索引（按商品去重，每个商品只保留首要分类） */
  products: SearchProduct[] = [];

  /** 分类 id → 分类 */
  private categoryById = new Map<number, any>();

  /** 聚合分类 id（如「全部」，包含几乎所有商品，仅用于展示） */
  private aggregateIds = new Set<number>();

  private loading: Promise<any[]> | null = null;

  /** 点击搜索结果 / 热搜 / 历史后，请求 /buy 页面定位到某商品 */
  private locateSubject = new Subject<number>();
  locateRequested$: Observable<number> = this.locateSubject.asObservable();

  /** 加载分类数据（请求去重 + 结果缓存），失败时返回空数组 */
  load(): Promise<any[]> {
    if (this.categories.length) {
      return Promise.resolve(this.categories);
    }
    if (this.loading) {
      return this.loading;
    }
    this.loading = this.api
      .findPlatformCategories()
      .then((r) => {
        this.categories = r?.data || [];
        return this.categories;
      })
      .catch(() => {
        this.categories = [];
        return this.categories;
      })
      .finally(() => {
        this.buildIndex();
        this.loading = null;
      });
    return this.loading;
  }

  private buildIndex() {
    const categories = this.categories || [];
    this.categoryById = new Map<number, any>();
    this.aggregateIds = new Set<number>();
    for (const c of categories) {
      if (c?.id !== undefined && c?.id !== null) {
        this.categoryById.set(c.id, c);
      }
    }

    // 识别聚合分类：名为「全部」，或收录了（几乎）所有商品
    const allIds = new Set<number>();
    for (const c of categories) {
      for (const p of c?.platforms || []) {
        if (p?.id !== undefined && p?.id !== null) {
          allIds.add(p.id);
        }
      }
    }
    for (const c of categories) {
      const isAggregate =
        (c?.name || '').trim() === '全部' ||
        (c?.platforms || []).length >= allIds.size;
      if (isAggregate) {
        this.aggregateIds.add(c?.id);
      }
    }

    // 按商品去重（同一商品会同时出现在「全部」与多个具体分类中），
    // 只收录一次，且优先挂在 category_id 指向的首要分类下
    const seen = new Set<number>();
    this.products = [];
    for (const c of categories) {
      if (this.aggregateIds.has(c?.id)) {
        continue;
      }
      for (const p of c?.platforms || []) {
        if (!p || seen.has(p.id)) {
          continue;
        }
        seen.add(p.id);
        this.pushProduct(p, this.primaryCategoryOf(p, c));
      }
    }
    // 兜底：只出现在聚合分类里的商品
    for (const c of categories) {
      if (!this.aggregateIds.has(c?.id)) {
        continue;
      }
      for (const p of c?.platforms || []) {
        if (!p || seen.has(p.id)) {
          continue;
        }
        seen.add(p.id);
        this.pushProduct(p, c);
      }
    }
  }

  /**
   * 商品的首要分类：优先取 category_id 指向的分类（非聚合、且确实包含该商品），
   * 否则用所在分类。「全部」聚合分类不参与首要分类判定，仅作兜底。
   */
  private primaryCategoryOf(p: any, fallback: any): any {
    const cid = p?.category_id;
    const byId =
      cid !== undefined && cid !== null ? this.categoryById.get(cid) : undefined;
    if (
      byId &&
      !this.aggregateIds.has(byId.id) &&
      (byId.platforms || []).some((x: any) => x?.id === p?.id)
    ) {
      return byId;
    }
    return fallback;
  }

  private pushProduct(p: any, category: any) {
    this.products.push({
      id: p.id,
      name: p.name || '',
      description: p.description || '',
      icon: p.icon || '',
      tags: p.tags || [],
      keywords: p.keywords || '',
      aliases: this.parseAliases(p.aliases),
      categoryName: category?.name || '',
      categoryId: category?.id,
    });
  }

  /**
   * 解析接口返回的 aliases 字段：
   * 兼容数组 / 逗号分隔字符串 / 缺失，统一成去空、去重、trim 后的字符串数组。
   */
  private parseAliases(raw: any): string[] {
    const list: any[] = Array.isArray(raw)
      ? raw
      : typeof raw === 'string' && raw.trim()
      ? raw.split(',')
      : [];
    return Array.from(
      new Set(
        list
          .map((x) => (x === null || x === undefined ? '' : String(x).trim()))
          .filter((x) => x)
      )
    );
  }

  /**
   * 商品所属分类（用于搜索定位）：
   * 优先返回商品 category_id 指向的首要分类（跳过聚合分类），
   * 找不到时返回第一个包含该商品且非「全部」聚合的分类；
   * 若只存在于聚合分类，则退回聚合分类；都不存在返回 null。
   */
  categoryOf(platformId: number): any | null {
    const product = this.products.find((p) => p.id === platformId);
    if (product) {
      const byId = this.categoryById.get(product.categoryId);
      if (byId && !this.aggregateIds.has(byId.id)) {
        return byId;
      }
    }
    const containing = this.categories.find(
      (c) =>
        !this.aggregateIds.has(c?.id) &&
        (c?.platforms || []).some((p: any) => p?.id === platformId)
    );
    if (containing) {
      return containing;
    }
    // 兜底：只收录在聚合分类（如「全部」）里的商品
    return (
      this.categories.find(
        (c) =>
          this.aggregateIds.has(c?.id) &&
          (c?.platforms || []).some((p: any) => p?.id === platformId)
      ) || null
    );
  }

  /** 取某商品名对应的别名列表 */
  aliasesOf(name: string): string[] {
    const key = (name || '').toLowerCase().replace(/\s+/g, '');
    return PLATFORM_SEARCH_ALIASES[key] || [];
  }

  /**
   * 搜索匹配（不区分大小写，中英文均可）。
   * 优先级：name === query > name 互相包含 > aliases/keywords/tags > description。
   */
  search(query: string): SearchProduct[] {
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      return [];
    }
    const scored: { p: SearchProduct; score: number }[] = [];
    for (const p of this.products) {
      const name = (p.name || '').toLowerCase();
      // 二级匹配素材：tags + keywords + 接口 aliases + 内置配置别名（合并使用）
      const extra = [
        ...(p.tags || []),
        p.keywords || '',
        ...(p.aliases || []),
        ...this.aliasesOf(p.name),
      ]
        .join(' ')
        .toLowerCase();
      const desc = (p.description || '').toLowerCase();

      let score = -1;
      if (name === q) {
        score = 0;
      } else if (name.includes(q) || q.includes(name)) {
        score = 1;
      } else if (extra && extra.includes(q)) {
        score = 2;
      } else if (desc && desc.includes(q)) {
        score = 3;
      }
      if (score >= 0) {
        scored.push({ p, score });
      }
    }
    scored.sort(
      (a, b) => a.score - b.score || a.p.name.localeCompare(b.p.name, 'zh')
    );
    return scored.map((o) => o.p);
  }

  /**
   * 为「一键直达」（热搜 / 历史）寻找最匹配的商品：
   * 优先精确同名，其次按搜索得分取最优。
   */
  findBest(keyword: string): SearchProduct | null {
    const list = this.search(keyword);
    if (!list.length) {
      return null;
    }
    const kw = (keyword || '').trim().toLowerCase();
    const exact = list.find((p) => p.name.toLowerCase() === kw);
    return exact || list[0];
  }

  /** 请求 /buy 页面定位到指定商品（切换分类 + 滚动 + 高亮） */
  locatePlatform(platformId: number) {
    this.locateSubject.next(platformId);
  }
}
